import { DurationUnitTypesEnum } from '../../lib/timeUtils';
import { EventSegment } from '../../types/entities/events';
import { ResourceTypeEnum } from '../../types/enums/resource';

export enum MaintenanceTimingEnum {
	BEFORE = 1,
	AFTER = 2,
	AT_THE_BEGINING = 3,
	AT_THE_END = 4,
}

export enum ReservationStatusEnum {
	PLANNED = 'Planned',
	APPROVED = 'Approved',
	AWAITING_ADMIN = 'Awaiting Admin',
	AWAITING_CUSTOMER = 'Awaiting Customer',
	REJECTED = 'Rejected',
	CANCELLED = 'Cancelled',
}

export enum PrivacySettingsEnum {
	PUBLIC = 'public',
	PRIVATE = 'private',
	HIDDEN = 'hidden',
}

export enum PaymentStatusEnum {
	NOT_PAID = 'not_paid',
	PARTIAL_PAYMENT = 'partial',
	FULLY_PAID = 'paid',
	REFUNDED = 'refunded',
	VOID = 'void',
}

export enum ReservationPaymentStatusExtendedEnum {
	PURCHASE_ORDER = 'purchase_order',
}

export enum SlotTypeEnum {
	EXTERNAL = 'external',
	INTERNAL = 'internal',
	MAINTENANCE = 'maintenance',
	CUSTOM = 'custom',
	PUNCH_PASS = 'punch_pass',
}

const EXTERNAL_SLOT_TYPES: ReadonlySet<string | undefined> = new Set<string | undefined>([SlotTypeEnum.EXTERNAL, SlotTypeEnum.PUNCH_PASS]);

/**
 * Whether the calendar should treat the slot type as an external (customer) booking. Punch pass slots are booked
 * by customers just like rentals, so they share the external behavior.
 * @param slotType {string} - slot type value
 * @return {boolean} - true for external and punch pass slots
 */
export function isExternalSlotType(slotType?: string): boolean {
	return EXTERNAL_SLOT_TYPES.has(slotType);
}

export interface ITimeSlot {
	resourceId: number;
	resourceName: string;
	startTime: string;
	endTime: string;
	startDate: string;
	endDate: string;
	relevantSlotId?: string;
}

export interface ColorCode {
	id: number;
	name: string;
	description?: string;
	lightValue: string;
	baseValue: string;
	darkValue: string;
}

export enum ColorCodeEnum {
	GRAY = 'gray',
	BLUE = 'blue',
}

export const colorCodeMap = {
	gray: {
		id: 1,
		name: 'neutral',
		lightValue: '#E2E7EB',
		baseValue: '#9B9B9B',
		darkValue: '#4E4E4E',
	},
	blue: {
		id: 6656,
		name: 'blue',
		lightValue: '#EDF4FC',
		baseValue: '#4A90E2',
		darkValue: '#254871',
	},
};

export interface IReservations {
	id?: number;
	creatorType: string;
	name: string;
	createdAt?: string;
}

export interface ICustomerName {
	firstName?: string;
	lastName?: string;
}

export interface ISpaceResource {
	name: string;
}

export interface IInstructorResource {
	firstName: string;
	lastName: string;
	resourceType: ResourceTypeEnum;
}

export interface IProductUser {
	productName: string;
}

export interface IAddon {
	totalPrice: number;
	productUser: IProductUser;
}

export interface ISlot {
	id: number;
	isReverted: boolean;
	reservationId: number;
	title?: string;
	creatorId?: number;
	creatorType?: string;
	userCreatorId?: number;
	startDate: string;
	endDate: string;
	startTime: string;
	endTime: string;
	timezone?: string;
	spaceId?: number;
	instructorsIds?: number[];
	deletedAt?: Date;
	publicNotes?: string;
	privateNotes?: string;
	slotType: SlotTypeEnum;
	productsUserId?: number;
	addonsProductUserIds?: number[] | null;
	segmentId: number;
	eventId: number;
	event: EventSegment;
	sportIds: number[];
	parentSlotId: number | null;
	parent?: ISlot;
	children?: ISlot[];
	maintenanceDurationdurationType: DurationUnitTypesEnum;
	maintenanceTiming: number;
	durationValue: number;
	seriesId?: number;
	invoiceId?: number;
	approvalStatus?: ReservationStatusEnum;
	displayName?: string;
	internalName?: string;
	activityTypes?: number[] | null;
	hourlyRate: number;
	totalPrice: number;
	isConsumerVisible: boolean;
	isMonitorVisible: boolean;
	isPrivate: boolean;
	occurrence?: number;
	maintenance?: ISlot[];
	productMetadata: {
		productId: number;
		price: number;
	};
	slotDurationType: string;
	previousProductUsersIds?: number[];
	conflictsCount?: number;
	conflicts?: ISlot[];
	isConnectedMaintenance?: boolean;
	connectedMaints: IMaintSlot[];
	overallStartTime?: string;
	overallEndTime?: string;
	colorCode: ColorCode;
	name: string;
	reservation: IReservations;
	isDraft?: boolean;
	paymentStatus: PaymentStatusEnum | ReservationPaymentStatusExtendedEnum;
	privacySetting?: PrivacySettingsEnum;
	maintenanceSlots?: ISlot[];
	resourceId: string;
	instructors?: { id: number; name: string }[];
	space: { id: number; name: string };
	resourceType: ResourceTypeEnum;
	colorCodeId?: number;
	hasApplications?: boolean;
	/**
	 * Used for calculations
	 */
	overallDuration?: number;
}
export interface IMaintSlot extends ISlot {
	maintenanceTiming: MaintenanceTimingEnum;
}

export type TPxHeightSetter = (px: number, isAfter: boolean) => void;

export type TMaintAtEndHeight = null | { height: number };

export interface IExpandedSlot extends ISlot {
	totalSlotsInReservation?: number;
	conflictCount?: number;
	customer?: ICustomerName;
	resource?: ISpaceResource;
	resources?: IInstructorResource[];
	productUser?: IProductUser;
	addons?: IAddon[];
}

export interface IPopupRedirections {
	goToConflicts?: (val: ITimeSlot) => void;
	goToReservation?: (reservationId: number) => void;
	goToReservationHistory?: (reservationId: number, reservationCreatedAt: string) => void;
}

export interface IAction {
	allow: boolean;
	handle?: ((...v: any[]) => void) | (() => void);
}

export interface IPopupActions {
	edit?: IAction;
	duplicate?: IAction;
	delete?: IAction;
	editNotes?: IAction;
	viewForms?: IAction;
}

export interface ISlotResource {
	id: number;
	name: string;
	resourceType: ResourceTypeEnum;
}
